import i18n from '../i18n/i18n';

/**
 * Pagination Constants
 * Default pagination configuration used across all AppDataTable instances
 */
export const PAGINATION = {
  DEFAULT_PAGE: 0, // Zero-indexed page number (first page)
  DEFAULT_PAGE_SIZE: 5, // Default number of records per page
} as const;

export const statusOptions = [
  { label: i18n.t('msg.active', 'Active'), code: true },
  { label: i18n.t('msg.inactive', 'Inactive'), code: false },
];

export const RECAPTCHA_ACTION_TYPE = {
  ONBOARDING: 'onboarding',
};

/**
 * Severity Styles Configuration
 * Centralized severity-based styling for modals, notifications, and UI feedback components
 * Used by: ConfirmationModal, PrerequisiteGuidance, and other severity-aware components
 * 
 * Severity Levels:
 * - primary: Brand purple theme (default, Classveew primary color)
 * - info: Blue theme (informational messages)
 * - success: Green theme (successful actions)
 * - warning: Yellow/amber theme (cautionary messages)
 * - danger/error: Red theme (destructive actions, errors)
 * 
 * Usage:
 * import { SEVERITY_STYLES } from '@/constant/main.constant';
 * const styles = SEVERITY_STYLES.warning;
 */
export const SEVERITY_STYLES = {
  primary: {
    icon: 'pi-info-circle',
    iconColor: 'text-project-purple',
    iconBorder: 'border-purple-300',
    bgColor: 'bg-purple-50',
    border: 'border-purple-300',
    messageColor: 'text-purple-700',
    warningBoxBg: 'bg-purple-50',
    warningBoxBorder: 'border-purple-200',
    warningBoxTextColor: 'text-purple-700',
    warningBoxIconColor: 'text-project-purple',
  },
  info: {
    icon: 'pi-info-circle',
    iconColor: 'text-blue-600',
    iconBorder: 'border-blue-300',
    bgColor: 'bg-blue-50',
    border: 'border-blue-300',
    messageColor: 'text-blue-700',
    warningBoxBg: 'bg-blue-50',
    warningBoxBorder: 'border-blue-200',
    warningBoxTextColor: 'text-blue-700',
    warningBoxIconColor: 'text-blue-600',
  },
  success: {
    icon: 'pi-check-circle',
    iconColor: 'text-green-600',
    iconBorder: 'border-green-300',
    bgColor: 'bg-green-50',
    border: 'border-green-300',
    messageColor: 'text-green-700',
    warningBoxBg: 'bg-green-50',
    warningBoxBorder: 'border-green-200',
    warningBoxTextColor: 'text-green-700',
    warningBoxIconColor: 'text-green-600',
  },
  warning: {
    icon: 'pi-exclamation-triangle',
    iconColor: 'text-yellow-600',
    iconBorder: 'border-yellow-400',
    bgColor: 'bg-yellow-50',
    border: 'border-yellow-300',
    messageColor: 'text-yellow-800',
    warningBoxBg: 'bg-yellow-50',
    warningBoxBorder: 'border-yellow-300',
    warningBoxTextColor: 'text-yellow-800',
    warningBoxIconColor: 'text-yellow-600',
  },
  danger: {
    icon: 'pi-times-circle',
    iconColor: 'text-red-600',
    iconBorder: 'border-red-300',
    bgColor: 'bg-red-50',
    border: 'border-red-300',
    messageColor: 'text-red-700',
    warningBoxBg: 'bg-red-50',
    warningBoxBorder: 'border-red-200',
    warningBoxTextColor: 'text-red-700',
    warningBoxIconColor: 'text-red-600',
  },
  error: {
    icon: 'pi-times-circle',
    iconColor: 'text-red-600',
    iconBorder: 'border-red-300',
    bgColor: 'bg-red-100',
    border: 'border-red-300',
    messageColor: 'text-red-700',
    warningBoxBg: 'bg-red-50',
    warningBoxBorder: 'border-red-200',
    warningBoxTextColor: 'text-red-700',
    warningBoxIconColor: 'text-red-700',
  },
} as const;

/**
 * Type definition for severity levels
 */
export type SeverityLevel = keyof typeof SEVERITY_STYLES;
